import SVReCaptcha from '@apps/www/src/www/components/SVReCaptcha';
import SVPageMeta from '@apps/www/src/www/containers/SVPageMeta';
import useAuthSettings from '@apps/www/src/www/hooks/useAuthSettings';
import useAuthUser from '@apps/www/src/www/hooks/useAuthUser';
import SVButton, { SVButtonUSES } from '@pkgs/shared-client/components/SVButton';
import SVForm from '@pkgs/shared-client/components/SVForm';
import { SVInputText, SVInputTextarea } from '@pkgs/shared-client/components/SVFormInputs';
import SVFormLayout from '@pkgs/shared-client/components/SVFormLayout';
import SVLink from '@pkgs/shared-client/components/SVLink';
import SVPageMargins from '@pkgs/shared-client/components/SVPageMargins';
import SVPageSubtitle from '@pkgs/shared-client/components/SVPageSubtitle';
import SVPageTitle from '@pkgs/shared-client/components/SVPageTitle';
import clientConfig from '@pkgs/shared-client/config';
import useEventCallback from '@pkgs/shared-client/hooks/useEventCallback';
import axios from 'axios';
import { useState } from 'react';

const ENABLE_RECAPTCHA = clientConfig.env === 'production';

const ContactPage = () => {
	const [emailSent, setEmailSent] = useState<boolean>(false);
	const [isCaptchaValid, setIsCaptchaValid] = useState<boolean | null>(null);
	const authUser = useAuthUser(['name']);
	const authSettings = useAuthSettings(['email']);
	const meta = {
		title: 'Contact us',
	};

	const handleSubmit = useEventCallback(
		async (values: { name: string; email: string; message: string }) => {
			if (ENABLE_RECAPTCHA && !isCaptchaValid) {
				setIsCaptchaValid(false);
				return;
			}

			const emailData = {
				data: values,
			};
			await axios.post('/api/sendEmail', emailData);
			setEmailSent(true);
		},
	);

	return (
		<>
			<SVPageMeta {...meta} />
			<SVPageMargins className="pt-20">
				<div className="mx-auto text-center">
					{emailSent ? (
						<>
							<SVPageTitle>Your message is on its way to us</SVPageTitle>
							<SVPageSubtitle>
								Thank you. We'll get back to you as soon as we're ready for you!
							</SVPageSubtitle>
						</>
					) : (
						<>
							<SVPageTitle>Contact us</SVPageTitle>
							<SVPageSubtitle>
								We'll get back to you as soon as we're ready for you!
							</SVPageSubtitle>
						</>
					)}
				</div>
				{!emailSent ? (
					<SVForm
						className="-sm:-mt-8 -sm:pt-20 mt-16 flex flex-col items-center justify-center"
						submitLabel={'Contact us'}
						onSubmit={handleSubmit}
						submitUse={SVButtonUSES.PRIMARY}
						initialValues={{
							name: authUser?.name || '',
							email: authSettings?.email || '',
							message: '',
						}}
					>
						<div className="text-muted mx-auto w-full max-w-[400px]">
							<SVFormLayout.Block className="mb-2">
								<SVFormLayout.P> What's your name?</SVFormLayout.P>
								<SVInputText name="name" required={true} disabled={!!authUser} />
							</SVFormLayout.Block>

							<SVFormLayout.Block className="mb-2">
								<SVFormLayout.P>What's your email address?</SVFormLayout.P>
								<SVInputText name="email" required={true} disabled={!!authUser} />
							</SVFormLayout.Block>

							<SVFormLayout.Block className="mb-2">
								<SVFormLayout.P>Message</SVFormLayout.P>
								<SVInputTextarea
									className="h-32"
									name="message"
									placeholder="Objectively describe your message to our team."
									required={true}
								/>
							</SVFormLayout.Block>
							{ENABLE_RECAPTCHA ? (
								<div className="mb-12 mt-4">
									<SVReCaptcha onChange={(value) => setIsCaptchaValid(!!value)} />

									{isCaptchaValid === false && (
										<div className="mb-2 mt-2 text-sm text-red-500">
											Please complete the reCAPTCHA
										</div>
									)}
								</div>
							) : null}
						</div>
					</SVForm>
				) : (
					<div className="mt-12 text-center">
						<SVButton Component={SVLink} to={'/'} use={SVButtonUSES.PRIMARY}>
							Back to home
						</SVButton>
					</div>
				)}
			</SVPageMargins>
		</>
	);
};

export default ContactPage;
